<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElButton, ElIcon } from 'element-plus'
import { Close } from '@element-plus/icons-vue'

/**
 * Slide-over navigation for phones and small tablets.
 *
 * Written by hand rather than with a component library's drawer. It is a
 * few lines, it is fully controlled, and its open/close behaviour can be
 * asserted without depending on a CSS transition completing — which is what
 * made the previous library's drawer stay mounted after a tap.
 */
const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()

const panel = ref<HTMLElement | null>(null)

function close(): void {
  open.value = false
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    close()
  }
}

watch(open, async (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', onKeydown)
    // Scrolling the page behind an open overlay is disorienting on a phone.
    document.body.style.overflow = 'hidden'

    // Move focus into the panel so a keyboard or screen reader user lands
    // where the menu is, not where the page behind it was.
    await nextTick()
    panel.value?.querySelector<HTMLElement>('a, button')?.focus()
  } else {
    document.removeEventListener('keydown', onKeydown)
    document.body.style.overflow = ''
  }
})

// A route change can unmount this while the menu is open; the listener and
// the scroll lock must not outlive the component.
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-50 lg:hidden">
      <!-- Backdrop. Not focusable: closing by keyboard is Escape. -->
      <div
        class="absolute inset-0 bg-black/40"
        aria-hidden="true"
        @click="close"
      />

      <aside
        ref="panel"
        class="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col overflow-y-auto bg-surface shadow-[--shadow-lg]"
        role="dialog"
        aria-modal="true"
        :aria-label="t('app.name')"
      >
        <div
          class="flex h-16 items-center justify-between border-b border-border px-4"
        >
          <p class="heading-sm text-ink">{{ t('app.name') }}</p>

          <ElButton
            class="touch-target"
            text
            circle
            :aria-label="t('nav.closeMenu')"
            @click="close"
          >
            <ElIcon><Close /></ElIcon>
          </ElButton>
        </div>

        <nav class="flex-1 bg-surface-raised px-4 pt-6 pb-4">
          <slot />
        </nav>
      </aside>
    </div>
  </Teleport>
</template>
